import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import './GSCConnectionModal.css';

const SEARCH_CONSOLE_SCOPES = [
  'https://www.googleapis.com/auth/webmasters.readonly',
  'https://www.googleapis.com/auth/userinfo.email',
  'https://www.googleapis.com/auth/userinfo.profile'
];

const GSCConnectionModal = ({ isOpen, onClose, onConnect, site, initialSites }) => {
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      const hasPresetSites = Array.isArray(initialSites) && initialSites.length > 0;
      setStep(hasPresetSites ? 2 : 1);
      setSites(hasPresetSites ? initialSites : []);
      setSelectedSite(hasPresetSites ? initialSites[0] : null);
      setError('');
      setLoading(false);
    } else {
      setSites([]);
      setSelectedSite(null);
      setError('');
      setLoading(false);
      setStep(1);
    }
  }, [isOpen, initialSites]);

  if (!isOpen || !site) {
    return null;
  }

  const handleGoogleAuth = () => {
    setLoading(true);
    setError('');

    try {
      const redirectUri = `${window.location.origin}/auth/google-oauth-callback`;
      const stateData = {
        siteId: site.siteId,
        currentUserId: user?.id || null,
        service: 'gsc'
      };

      const authUrl =
        `https://accounts.google.com/o/oauth2/v2/auth?` +
        `client_id=${process.env.REACT_APP_GOOGLE_CLIENT_ID}&` +
        `redirect_uri=${encodeURIComponent(redirectUri)}&` +
        `response_type=code&` +
        `scope=${encodeURIComponent(SEARCH_CONSOLE_SCOPES.join(' '))}&` +
        `access_type=offline&` +
        `prompt=consent&` +
        `state=${encodeURIComponent(JSON.stringify(stateData))}`;

      window.location.href = authUrl;
    } catch (authError) {
      console.error('GSC auth error:', authError);
      setError('Authentication failed. Please try again.');
      setLoading(false);
    }
  };

  const handleConnect = () => {
    if (!selectedSite) {
      setError('Please select a Search Console property from the list.');
      return;
    }

    onConnect({
      siteUrl: selectedSite.siteUrl,
      permissionLevel: selectedSite.permissionLevel,
      verificationState: selectedSite.verificationState,
      propertyType: selectedSite.propertyType
    });
    onClose();
  };

  const renderStepIndicator = (currentStep) => (
    <div className="gsc-step-indicator">
      <div className={`gsc-step ${currentStep >= 1 ? 'active' : ''}`}>1</div>
      <div className={`gsc-step-line ${currentStep > 1 ? 'completed' : ''}`}></div>
      <div className={`gsc-step ${currentStep >= 2 ? 'active' : ''}`}>2</div>
    </div>
  );

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content gsc-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{step === 1 ? 'Connect Google Search Console' : 'Select Property'}</h2>
          <button className="modal-close" onClick={onClose} aria-label="Close">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="modal-body">
          {step === 1 && (
            <div className="gsc-auth-step">
              {renderStepIndicator(1)}
              <div className="gsc-auth-content">
                <div className="gsc-auth-icon">
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 3C7 3 3 7 3 12s4 9 9 9 9-4 9-9" />
                    <path d="M12 7v5l3 3" />
                  </svg>
                </div>

                <h3>Authorize Search Console Access</h3>
                <p>
                  We need permission to view your Google Search Console properties so we can pull search performance
                  data for <strong>{site.name}</strong>.
                </p>

                {error && <div className="error-message">{error}</div>}

                <button className="btn-google-auth" onClick={handleGoogleAuth} disabled={loading}>
                  {loading ? (
                    <>
                      <div className="spinner-small"></div>
                      Connecting...
                    </>
                  ) : (
                    <>
                      <svg className="google-icon" viewBox="0 0 24 24">
                        <path
                          fill="#4285F4"
                          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                        />
                        <path
                          fill="#34A853"
                          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                        />
                        <path
                          fill="#FBBC05"
                          d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                        />
                        <path
                          fill="#EA4335"
                          d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                        />
                      </svg>
                      Connect with Google
                    </>
                  )}
                </button>

                <div className="gsc-auth-info">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                    <path
                      fillRule="evenodd"
                      d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm.93-9.412-1 4.705c-.07.34.029.533.304.533.194 0 .487-.07.686-.246l-.088.416c-.287.346-.92.598-1.465.598-.703 0-1.002-.422-.808-1.319l.738-3.468c.064-.293.006-.399-.287-.47l-.451-.081.082-.381 2.29-.287zM8 5.5a1 1 0 1 0 0-2 1 1 0 0 0 0 2z"
                    />
                  </svg>
                  <span>We only request read-only access to your Search Console data.</span>
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="gsc-selection-step">
              {renderStepIndicator(2)}
              <div className="gsc-selection-content">
                <h3>Select Search Console Property</h3>
                <p>
                  Choose which Search Console property to connect to <strong>{site.name}</strong>
                </p>

                {error && <div className="error-message">{error}</div>}

                {sites.length > 0 ? (
                  <div className="gsc-sites-list">
                    {sites.map((item) => (
                      <button
                        type="button"
                        key={item.siteUrl}
                        className={`gsc-site-item ${
                          selectedSite?.siteUrl === item.siteUrl ? 'selected' : ''
                        }`}
                        onClick={() => {
                          setSelectedSite(item);
                          setError('');
                        }}
                      >
                        <div className="gsc-site-radio">
                          {selectedSite?.siteUrl === item.siteUrl && <div className="radio-inner" />}
                        </div>
                        <div className="gsc-site-info">
                          <span className="gsc-site-url">{item.siteUrl}</span>
                          <span className="gsc-site-meta">
                            {item.permissionLevel || 'Unknown access'}
                            {item.verificationState ? ` • ${item.verificationState}` : ''}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="gsc-empty-state">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" />
                      <path d="M8 15s1.5-2 4-2 4 2 4 2" />
                      <line x1="9" y1="9" x2="9.01" y2="9" />
                      <line x1="15" y1="9" x2="15.01" y2="9" />
                    </svg>
                    <p>
                      We couldn&apos;t find any Search Console properties. Please make sure you have verified properties
                      in your Google Search Console account and try again.
                    </p>
                  </div>
                )}
              </div>

              <div className="modal-actions">
                <button
                  className="btn-secondary"
                  onClick={() => {
                    setStep(1);
                    setSites([]);
                    setSelectedSite(null);
                    setError('');
                  }}
                >
                  Back
                </button>
                <button className="btn-primary" onClick={handleConnect}>
                  Connect Property
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GSCConnectionModal;

